import { convertToUnit } from 'src/helpers/units_helpers';
import { checkForStatisticError } from '../Chart/chart_helper';

/* Calculate "feels‑like" temperature according to the official formulas.
 *
 * @param temp   Air temperature(°C | °F)
 * @param rh      Relative humidity 0‑100(%)
 * @param windMs  Wind speed at sensor height(m s⁻¹) – optional, default 0
 * @returns Apparent temperature in °C | °F rounded to 0.1
 */
export function feelsLike(temp: number, rh: number, windMs: number, unit = 'C'): string {
	if (checkForStatisticError(temp) || checkForStatisticError(rh)) return `N/A ${unit}`;

	const tempC = unit === 'F' ? ((temp - 32) * 5) / 9 : temp;
	/* --------------------------------------------------
	 * 1) Wind‑Chill (cold + wind)
	 * -------------------------------------------------- */
	if (tempC <= 10 && windMs >= 1.34) {
		const vKmh = windMs * 3.6; // m s⁻¹ → km h⁻¹
		const feelsLike =
			13.12 +
			0.6215 * tempC -
			11.37 * Math.pow(vKmh, 0.16) +
			0.3965 * tempC * Math.pow(vKmh, 0.16);
		return normalizeFeelsLike(tempC, feelsLike, unit);
	}

	/* --------------------------------------------------
	 * 2) Heat‑Index (hot + humid)
	 * -------------------------------------------------- */
	if (tempC >= 27 && rh >= 40) {
		return normalizeFeelsLike(tempC, heatIndexC(tempC, rh), unit);
	}

	/* --------------------------------------------------
	 * 3) Steadman Apparent Temperature – works for the rest
	 * -------------------------------------------------- */
	const vapourPressure = (rh / 100) * 6.105 * Math.exp((17.27 * tempC) / (237.7 + tempC));
	const feelsLike = tempC + 0.33 * vapourPressure - 0.7 * windMs - 4.0;
	return normalizeFeelsLike(tempC, feelsLike, unit);
}

/**
 * Calculates the maximum allowable deviation from the actual temperature
 * for the "feels-like" temperature calculation. The deviation varies
 * based on the input temperature.
 *
 * @param t Temperature in °C.
 * @returns Maximum deviation in °C.
 */

const dynamicClamp = (tempC: number) => {
	if (tempC <= 27) return 8;
	return 4 + 0.25 * (tempC - 27);
};

/**
 * Limits the "feels-like" temperature to a reasonable range around the actual
 * temperature, based on the actual temperature itself.
 *
 * @param tempC   Actual temperature in °C.
 * @param feels   Calculated "feels-like" temperature in °C.
 * @returns The "feels-like" temperature, limited to the acceptable range.
 */
const normalizeFeelsLike = (tempC: number, feels: number, unit: string) => {
	const maxDiff = dynamicClamp(tempC);
	const t = Math.min(tempC + maxDiff, Math.max(tempC - maxDiff, feels));
	return convertToUnit(unit === 'F' ? (t * 9) / 5 + 32 : t, `°${unit}`);
};

/** Heat‑Index in °C using Rothfusz regression (original in Fahrenheit). */
const heatIndexC = (tempC: number, rh: number): number => {
	// Convert to °F because the coefficients come from the original formula
	const tF = (tempC * 9) / 5 + 32; // °F
	const hiSimpleF = 0.5 * (tF + 61.0 + (tF - 68.0) * 1.2 + rh * 0.094);
	if (hiSimpleF < 80) return ((hiSimpleF - 32) * 5) / 9;
	let hiF =
		-42.379 +
		2.04901523 * tF +
		10.14333127 * rh -
		0.22475541 * tF * rh -
		0.00683783 * tF ** 2 -
		0.05481717 * rh ** 2 +
		0.00122874 * tF ** 2 * rh +
		0.00085282 * tF * rh ** 2 -
		0.00000199 * tF ** 2 * rh ** 2;
	// Adjustment for very low RH (<13 %) & high T (80–112 °F)
	if (rh < 13 && tF >= 80 && tF <= 112) {
		hiF -= ((13 - rh) / 4) * Math.sqrt((17 - Math.abs(tF - 95)) / 17);
	}
	// Adjustment for very high RH (>85 %) & moderate T (80–87 °F)
	else if (rh > 85 && tF >= 80 && tF <= 87) {
		hiF += ((rh - 85) / 10) * ((87 - tF) / 5);
	}
	// Back to °C
	return ((hiF - 32) * 5) / 9;
};
